/**
 * Choosing a folder by typing it.
 *
 * Every folder in this app was chosen through the native dialog, which is the
 * wrong and only way round for some of the people who use it: a path arrives
 * in an email, in a terminal, in a protocol document, and navigating a dialog
 * to a place you can already name is busywork. Worse on a server mount, where
 * the dialog takes seconds to list a directory somebody could have pasted.
 *
 * So both. The field takes a path — pasted, typed, or dragged in — and the
 * button opens the dialog for when browsing is the point. `~` is expanded
 * because that is how people write a home path, and the app is the only thing
 * here that knows it is not a literal directory name.
 */

import { useEffect, useState } from "react";
import { FolderOpen, Loader2 } from "lucide-react";

import { Button, Input } from "@/components/ui/primitives";
import { pickDirectory } from "@/ipc/api";
import { cn } from "@/lib/utils";
import { cleanPath, homeFrom } from "./paths";

export function PathField({
  value,
  placeholder,
  busy,
  title,
  onSubmit,
  className,
}: {
  value?: string;
  placeholder?: string;
  busy?: boolean;
  /** The dialog's title, when the button is used instead. */
  title: string;
  onSubmit: (path: string) => void;
  className?: string;
}) {
  const [text, setText] = useState(value ?? "");
  const [home, setHome] = useState<string | null>(null);

  useEffect(() => setText(value ?? ""), [value]);

  // Learned from any absolute path the app already knows, rather than asked
  // for: there is no "home directory" IPC and inventing one would be a guess.
  useEffect(() => setHome((h) => homeFrom(value) ?? h), [value]);

  const submit = () => {
    const path = cleanPath(text, home);
    if (path) onSubmit(path);
  };

  const browse = async () => {
    const { path } = await pickDirectory(title, text || undefined);
    if (path) {
      setText(path);
      onSubmit(path);
    }
  };

  return (
    <div className={cn("flex min-w-0 items-center gap-1.5", className)}>
      <Input
        value={text}
        placeholder={placeholder ?? "/path/to/your/subjects — or paste one"}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
        }}
        className="min-w-0 flex-1 font-mono text-[11.5px]"
        spellCheck={false}
      />
      <Button variant="outline" className="shrink-0" onClick={() => void browse()} disabled={busy}>
        <FolderOpen className="h-3.5 w-3.5" />
        Browse…
      </Button>
      <Button
        variant="primary"
        className="shrink-0"
        onClick={submit}
        disabled={busy || !text.trim()}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
        Open
      </Button>
    </div>
  );
}
